import React, { useEffect, useMemo, useState } from "react";
import "./HeroSection.css";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/bundle";
import { EffectFade, Autoplay, Pagination } from "swiper/modules";
import collection1 from "../../../Assets/Collection/collection1.jpg";
import collection2 from "../../../Assets/Collection/collection2.jpg";
import collection3 from "../../../Assets/Collection/collection3.jpg";
import { getImageUrl } from "../../../api/client";
import { sanitizeStorefrontUrl } from "../../../utils/storefrontUrl";

const defaultSlides = [
  { label: "Designer Rental", title: "Rent the Look", cta: "Explore Collection", ctaLink: "/shop", image: collection1, alt: "Designer clothing rental" },
  { label: "New Season", title: "Dresses & More", cta: "View Collection", ctaLink: "/shop", image: collection2, alt: "New season" },
  { label: "Book Your Style", title: "3 to 10 Day Rentals", cta: "How It Works", ctaLink: "/about", image: collection3, alt: "Rent 3-10 days" },
];

const HeroSection = (props) => {
  const slides = useMemo(
    () =>
      props?.props?.slides && props.props.slides.length > 0
        ? props.props.slides.map((s) => ({
            ...s,
            image: getImageUrl(s.imageUrl || s.image),
            ctaLink: sanitizeStorefrontUrl(s.ctaLink || "/shop") || "/shop",
          }))
        : defaultSlides,
    [props?.props?.slides]
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const hasEnoughSlides = slides && slides.length > 0;
  const slideCount = slides?.length ?? 0;
  const enableLoop = slideCount > 1;

  if (!hasEnoughSlides) return null;

  return (
    <section
      className="heroMain heroSliderWrap"
      aria-roledescription="carousel"
      aria-label="Featured collections"
    >
      <Swiper
        className="heroSwiper heroSwiperFade"
        slidesPerGroup={1}
        slidesPerGroupSkip={0}
        watchOverflow
        observer
        observeParents
        resizeObserver
        modules={[EffectFade, Autoplay, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop={false}
        rewind={enableLoop}
        onBeforeDestroy={(swiper) => {
          swiper.autoplay?.stop?.();
        }}
        autoplay={
          prefersReducedMotion || slideCount <= 1
            ? false
            : {
                delay: 5500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
        }
        pagination={{
          clickable: true,
          dynamicBullets: false,
        }}
        speed={prefersReducedMotion ? 150 : 900}
        grabCursor={!prefersReducedMotion}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id || `hero-${index}`}>
            <div className="heroSlide">
              <div className="heroSlideMedia">
                <img
                  src={slide.image}
                  alt={slide.alt || ""}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  sizes="100vw"
                />
              </div>
              <div className="heroSlideContent">
                <p className="heroLabel">{slide.label}</p>
                <h2 className="heroTitle">{slide.title}</h2>
                <Link
                  to={slide.ctaLink || "/shop"}
                  className="heroCta"
                  onClick={scrollToTop}
                >
                  {slide.cta}
                </Link>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default HeroSection;
